import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useNavigate } from 'react-router-dom';
import { GripVertical, Zap } from 'lucide-react';
import type { PipelineEntry } from '../../types/pipeline';

interface PipelineCandidateCardProps {
  entry: PipelineEntry;
  missionId: number;
  /** When true, renders a static clone inside DragOverlay (no DnD hooks active) */
  isOverlay?: boolean;
}

const getMatchBadgeStyle = (score: number): string => {
  if (score >= 80) return 'bg-emerald-100 text-emerald-700 border-emerald-200';
  if (score >= 60) return 'bg-amber-100 text-amber-700 border-amber-200';
  return 'bg-slate-100 text-slate-500 border-slate-200';
};

const getAvailabilityBadge = (status: string | null): { label: string; style: string } => {
  if (status === 'disponible') return { label: 'Disponible', style: 'bg-emerald-100 text-emerald-700' };
  if (status === 'listening') return { label: 'Sous préavis', style: 'bg-amber-100 text-amber-700' };
  return { label: 'Non disponible', style: 'bg-slate-100 text-slate-500' };
};

/**
 * PipelineCandidateCard — sortable candidate card for the ATS pipeline Kanban.
 *
 * DnD strategy: drag handle (GripVertical) receives {...listeners} — card body
 * receives onClick for navigation. This prevents click/drag conflicts (Pitfall 3).
 *
 * Navigation: D-11, D-13, D-14 — navigate('/talents/:id', { state: { fromMissionId } })
 * Route /candidates/:id does not exist in App.tsx — /talents/:id is the existing TalentPoolView route.
 */
export const PipelineCandidateCard: React.FC<PipelineCandidateCardProps> = ({
  entry,
  missionId,
  isOverlay = false,
}) => {
  const navigate = useNavigate();

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: String(entry.matchId),
    data: { stage: entry.stage, candidateId: entry.candidateId },
    disabled: isOverlay,
  });

  const style = isOverlay
    ? { transform: 'rotate(2deg) scale(0.9)', opacity: 0.9 }
    : {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.4 : 1,
        touchAction: 'none' as const,
      };

  const initials = entry.fullName
    .split(' ')
    .map((w) => w[0] ?? '')
    .join('')
    .substring(0, 2)
    .toUpperCase();

  const handleCardClick = () => {
    if (isDragging) return;
    // D-13: navigate to candidate profile. D-14: pass missionId via navigation state.
    // Route /candidates/:id is not defined in App.tsx — use /talents/:id (existing route).
    navigate(`/talents/${entry.candidateId}`, {
      state: { fromMissionId: missionId },
    });
  };

  const availability = getAvailabilityBadge(entry.availabilityStatus);
  const scoreBadge = getMatchBadgeStyle(entry.matchScore);

  return (
    <div
      ref={isOverlay ? undefined : setNodeRef}
      style={style}
      {...(isOverlay ? {} : attributes)}
      className={`
        group bg-white p-4 rounded-xl border border-slate-200 shadow-sm
        transition-all duration-200 relative select-none
        hover:shadow-lg hover:-translate-y-0.5 hover:border-amber-300
        ${isOverlay ? 'shadow-2xl' : ''}
      `}
    >
      {/* Drag handle — listeners isolated here to prevent click/drag conflict (Pitfall 3) */}
      {!isOverlay && (
        <div
          {...listeners}
          className="absolute top-3 right-3 p-1 rounded text-slate-300 hover:text-slate-500 cursor-grab active:cursor-grabbing transition-colors"
          title="Déplacer"
        >
          <GripVertical size={14} />
        </div>
      )}

      {/* Card body — click navigates to candidate profile */}
      <div onClick={handleCardClick} className="cursor-pointer">
        {/* Header: avatar + name + title */}
        <div className="flex items-start gap-3 mb-3 pr-6">
          <div className="w-9 h-9 bg-slate-50 border border-slate-100 rounded-lg flex items-center justify-center font-bold text-slate-600 text-xs shrink-0 group-hover:border-amber-200 transition-colors shadow-sm">
            {initials}
          </div>
          <div className="min-w-0">
            {/* D-08: full name */}
            <p className="text-base font-bold text-slate-900 line-clamp-1 leading-tight group-hover:text-amber-600 transition-colors">
              {entry.fullName}
            </p>
            {/* D-08: current title */}
            {entry.currentTitle && (
              <p className="text-xs text-slate-500 font-medium line-clamp-1 mt-0.5">
                {entry.currentTitle}
              </p>
            )}
          </div>
        </div>

        {/* Footer: availability + score */}
        <div className="flex items-center justify-between gap-2">
          {/* D-08: availability */}
          <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${availability.style}`}>
            {availability.label}
          </span>

          {/* D-08: match score */}
          <span className={`flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full border ${scoreBadge}`}>
            <Zap size={10} fill="currentColor" />
            {entry.matchScore}%
          </span>
        </div>
      </div>
    </div>
  );
};
